/**
 * AI ops surface (ADR-054/058/064/065) — agent switches, the runs audit, risk
 * alerts, proposals and the executive digest.
 *
 * Own module per the B3 convention. One property every caller must handle:
 * the whole `/internal/admin/ai/*` surface exists ONLY when `AI_OPS_ENABLED`
 * is set on the server — with the layer off these routes 404. `aiOff(err)`
 * names that state so the page can say "the AI layer is off" honestly instead
 * of showing an error where there is only a switch.
 */
import { http, ApiError } from './http.ts';
import { MOCK_MODE } from './client.ts';

/** True when the error means the AI layer is not registered at all. */
export function aiOff(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

export interface AiAgent {
  agent: string;
  enabled: boolean;
  model: string;
  dailyBudgetUsd: string;
  updatedAt: string;
}

export interface AiRunView {
  id: number;
  agent: string;
  model: string;
  inputDigest: string;
  prompt: string;
  output: string | null;
  tokensIn: number | null;
  tokensOut: number | null;
  /** USD, 6-place decimal string. */
  costUsd: string | null;
  latencyMs: number | null;
  status: 'ok' | 'error' | 'refused' | 'budget_denied';
  refMeta: Record<string, unknown> | null;
  createdAt: string;
}

export interface OpsAlertView {
  id: number;
  kind: string;
  scope: string | null;
  refId: number | null;
  detail: Record<string, unknown> | null;
  status: 'open' | 'acked';
  ackedBy: number | null;
  createdAt: string;
}

export interface AiProposalView {
  id: number;
  agent: string;
  kind: string;
  refType: string | null;
  refId: number | null;
  detail: Record<string, unknown> | null;
  status: 'open' | 'armed' | 'dismissed';
  decidedBy: number | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface AiDigestView {
  id: number;
  kind: 'daily' | 'weekly';
  periodStart: string;
  /** The prose. Empty with figures present = the model half was skipped. */
  body: string;
  inputJson: Record<string, unknown> | null;
  createdAt: string;
}

/** LIVE — `GET /internal/admin/ai/agents`. */
export async function fetchAiAgents(): Promise<AiAgent[]> {
  if (MOCK_MODE) return [];
  const body = await http.get<{ agents: AiAgent[] }>('/internal/admin/ai/agents');
  return body.agents;
}

/** LIVE — `PUT /internal/admin/ai/agents/:agent`. Admin, audited (`ai.agent.toggle`). */
export async function setAiAgentEnabled(agent: string, enabled: boolean): Promise<void> {
  await http.put<{ agent: string; enabled: boolean }>(
    `/internal/admin/ai/agents/${encodeURIComponent(agent)}`,
    { enabled },
  );
}

/** LIVE — `GET /internal/admin/ai/runs`. Keyset-paginated, newest first. */
export async function fetchAiRuns(query: {
  agent?: string;
  status?: AiRunView['status'];
  limit?: number;
  before?: number;
} = {}): Promise<{ runs: AiRunView[]; nextBefore: number | null }> {
  if (MOCK_MODE) return { runs: [], nextBefore: null };
  return http.get<{ runs: AiRunView[]; nextBefore: number | null }>('/internal/admin/ai/runs', {
    agent: query.agent,
    status: query.status,
    limit: query.limit ?? 50,
    before: query.before,
  });
}

/** LIVE — `GET /internal/admin/ai/alerts`. */
export async function fetchAiAlerts(status?: 'open' | 'acked'): Promise<OpsAlertView[]> {
  if (MOCK_MODE) return [];
  const body = await http.get<{ alerts: OpsAlertView[] }>('/internal/admin/ai/alerts', { status });
  return body.alerts;
}

/** LIVE — `POST /internal/admin/ai/alerts/:id/ack`. Admin, audited. */
export async function ackAiAlert(id: number): Promise<void> {
  await http.post<{ id: number; status: string }>(`/internal/admin/ai/alerts/${id}/ack`);
}

/** LIVE — `GET /internal/admin/ai/proposals`. */
export async function fetchAiProposals(status?: 'open' | 'armed' | 'dismissed'): Promise<AiProposalView[]> {
  if (MOCK_MODE) return [];
  const body = await http.get<{ proposals: AiProposalView[] }>('/internal/admin/ai/proposals', { status });
  return body.proposals;
}

/**
 * LIVE — `POST /internal/admin/ai/proposals/:id/decision`. Admin, audited.
 * Deciding moves NO money: `armed` records "I acted, through the real tools".
 */
export async function decideAiProposal(id: number, status: 'armed' | 'dismissed'): Promise<void> {
  await http.post<{ id: number; status: string }>(`/internal/admin/ai/proposals/${id}/decision`, { status });
}

/** LIVE — `GET /internal/admin/ai/digests`. Newest first. */
export async function fetchAiDigests(kind?: 'daily' | 'weekly'): Promise<AiDigestView[]> {
  if (MOCK_MODE) return [];
  const body = await http.get<{ digests: AiDigestView[] }>('/internal/admin/ai/digests', { kind, limit: 14 });
  return body.digests;
}
